/**
 * Punkt 150 P1b (KONZEPT-SPEISEKARTEN-WIDGET § 4b.2): Kartenart im Import-Assistenten.
 *
 * „Kartenart“ = Mittagskarte, Abendkarte, Wochenkarte … (frei, `menu_card_types`). Nicht zu
 * verwechseln mit dem „Sortiment“ (`menu_cards.kind`: Speisen/Getränke/…).
 *
 * Ablauf: Art waehlen (oder neu anlegen; bei leerer Liste Startvorschlaege) → Weiche nach Anzahl
 * Karten in dieser Art: 0 = neue Karte; ab 1 = Frage „Ersetzen oder zusaetzliche Karte?“ (nie
 * stillschweigend); ab 2 + Ersetzen = Auswahl mit Name, Stand und Gueltigkeit. Technisch bleibt es
 * der bestehende `cardMode` (create/replace) — die Seite haelt den Zustand, hier nur die Anzeige.
 * Der KI-Vorschlag ist nur Vorauswahl; die Art stammt immer aus der Mandanten-Liste.
 */
import { useState } from "react";
import type { MenuCard, MenuCardType } from "./types";
import { Button, Icon, Input, Radio, Select } from "./components";

export const KARTENART_STARTWERTE = ["Mittagskarte", "Abendkarte", "Wochenkarte"] as const;
export type KartenartAntwort = "ersetzen" | "zusaetzlich" | null;

const KEINE = "__keine__";
const NEU = "__neu__";

function datum(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

export function gueltigkeit(k: Pick<MenuCard, "validFrom" | "validUntil">): string {
  if (!k.validFrom && !k.validUntil) return "unbegrenzt gültig";
  if (k.validFrom && k.validUntil) return `gültig ${datum(k.validFrom)}–${datum(k.validUntil)}`;
  return k.validFrom ? `gültig ab ${datum(k.validFrom)}` : `gültig bis ${datum(k.validUntil)}`;
}

export function KartenartWahl({
  types,
  cards,
  cardTypeId,
  suggestion,
  antwort,
  replaceCardId,
  onCardType,
  onAntwort,
  onReplaceCard,
  onCreateType,
}: {
  types: MenuCardType[];
  cards: MenuCard[];
  cardTypeId: string | null;
  suggestion: { cardTypeId: string; name: string } | null;
  antwort: KartenartAntwort;
  replaceCardId: string;
  onCardType: (id: string | null) => void;
  onAntwort: (a: Exclude<KartenartAntwort, null>) => void;
  onReplaceCard: (cardId: string) => void;
  /** Legt die Art per POST an; die Seite waehlt sie danach aus. */
  onCreateType: (name: string) => Promise<void>;
}) {
  const [neuOffen, setNeuOffen] = useState(false);
  const [neuName, setNeuName] = useState("");
  const [busy, setBusy] = useState(false);
  const aktiv = types.filter((t) => !t.archivedAt);
  const art = aktiv.find((t) => t.id === cardTypeId) ?? null;
  const karten = art ? cards.filter((c) => c.cardTypeId === art.id) : [];

  async function anlegen(name: string) {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreateType(name.trim());
      setNeuOffen(false);
      setNeuName("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="kartenart-wahl">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-label-md text-label-md text-on-background dark:text-inverse-on-surface">Kartenart</span>
        {aktiv.length > 0 && (
          <div className="min-w-[220px] max-w-[280px]">
            <Select
              ariaLabel="Kartenart"
              value={neuOffen ? NEU : (art?.id ?? KEINE)}
              onValueChange={(v) => {
                if (v === NEU) return setNeuOffen(true);
                setNeuOffen(false);
                onCardType(v === KEINE ? null : v);
              }}
              options={[
                { value: KEINE, label: "Ohne Kartenart" },
                ...aktiv.map((t) => ({ value: t.id, label: t.name })),
                { value: NEU, label: "Neue Kartenart …" },
              ]}
            />
          </div>
        )}
        {suggestion && art?.id === suggestion.cardTypeId && (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-body-sm text-[12px] text-primary">
            <Icon name="auto_awesome" className="text-[14px]" />
            Vorschlag der KI – du kannst ihn ändern
          </span>
        )}
      </div>

      {aktiv.length === 0 && !neuOffen && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Kartenarten. Vorschläge:</span>
          {KARTENART_STARTWERTE.map((n) => (
            <Button key={n} type="button" variant="secondary" disabled={busy} onClick={() => void anlegen(n)}>
              {n}
            </Button>
          ))}
          <Button type="button" variant="ghost" onClick={() => setNeuOffen(true)}>
            Eigene Kartenart …
          </Button>
        </div>
      )}

      {neuOffen && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="max-w-[260px]"
            aria-label="Name der neuen Kartenart"
            placeholder="z. B. Brunchkarte"
            value={neuName}
            onChange={(e) => setNeuName(e.target.value)}
          />
          <Button type="button" disabled={busy || !neuName.trim()} onClick={() => void anlegen(neuName)}>
            Anlegen
          </Button>
          <Button type="button" variant="ghost" onClick={() => setNeuOffen(false)}>
            Abbrechen
          </Button>
        </div>
      )}

      {art && karten.length === 0 && (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Noch keine {art.name} vorhanden – der Import wird als neue Karte angelegt.
        </p>
      )}

      {art && karten.length > 0 && (
        <div className="rounded-lg border border-outline-variant/40 px-3 py-2.5">
          <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            {karten.length === 1
              ? `Es gibt schon eine ${art.name} („${karten[0]!.name}“). Ersetzen oder zusätzliche Karte?`
              : `Es gibt schon ${karten.length} Karten der Art ${art.name}. Ersetzen oder zusätzliche Karte?`}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
            <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
              <Radio name="kartenart-antwort" checked={antwort === "ersetzen"} onChange={() => onAntwort("ersetzen")} />
              {karten.length === 1 ? `„${karten[0]!.name}“ ersetzen` : "Eine davon ersetzen"}
            </label>
            <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm">
              <Radio name="kartenart-antwort" checked={antwort === "zusaetzlich"} onChange={() => onAntwort("zusaetzlich")} />
              Zusätzliche Karte anlegen
            </label>
          </div>
          {antwort === "ersetzen" && karten.length > 1 && (
            <fieldset className="mt-3 space-y-1.5">
              <legend className="mb-1 font-body-sm text-body-sm text-on-surface-variant">Welche Karte soll ersetzt werden?</legend>
              {karten.map((k) => (
                <label key={k.id} className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-surface-container-low">
                  <Radio name="kartenart-ersetzen" className="mt-0.5" checked={replaceCardId === k.id} onChange={() => onReplaceCard(k.id)} />
                  <span className="font-body-sm text-body-sm">
                    <span className="font-semibold">{k.name}</span>
                    <span className="text-on-surface-variant">
                      {" · "}geändert {datum(k.updatedAt)} · {k.itemCount ?? 0} Positionen · {gueltigkeit(k)}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
}

/** Einmalige, wegklickbare Aufforderung fuer Bestandskarten ohne Kartenart (§ 4b.1). */
const AUFFORDERUNG_KEY = "maestro.kartenart-aufforderung-weg";

export function lesenAufforderungWeg(): boolean {
  try {
    return window.localStorage.getItem(AUFFORDERUNG_KEY) === "1";
  } catch {
    return false;
  }
}

export function KartenartAufforderung({ ohneArt, onWeg }: { ohneArt: number; onWeg: () => void }) {
  if (ohneArt === 0) return null;
  return (
    <div className="flex items-start gap-3 rounded-lg bg-primary/5 px-3 py-2.5" role="note">
      <Icon name="label" className="mt-0.5 text-[18px] text-primary" />
      <p className="min-w-0 flex-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
        {ohneArt === 1 ? "Eine deiner Karten hat" : `${ohneArt} deiner Karten haben`} noch keine Kartenart (z. B. Mittagskarte).
        Wenn du eine davon per Import ergänzt oder ersetzt, wähle oben die Kartenart – sie wird der Karte dann zugeordnet.
      </p>
      <Button
        type="button"
        variant="ghost"
        aria-label="Hinweis ausblenden"
        onClick={() => {
          try {
            window.localStorage.setItem(AUFFORDERUNG_KEY, "1");
          } catch {
            /* ohne Speicher: nur fuer diese Sitzung ausgeblendet */
          }
          onWeg();
        }}
      >
        <Icon name="close" className="text-[16px]" />
      </Button>
    </div>
  );
}
